import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const UserProfile = () => {
  const { user, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    userName: '',
    userMobileNo: '',
    companyName: '',
    role: 'visitor'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        userName: user.userName || '',
        userMobileNo: user.userMobileNo || '',
        companyName: user.companyName || '',
        role: user.role || 'visitor'
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.userName.trim()) {
      setSnackbar({
        open: true,
        message: 'Name is required',
        severity: 'info'
      });
      return false;
    }

    if (formData.userMobileNo && !/^\d{10,12}$/.test(formData.userMobileNo)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid mobile number (10-12 digits)',
        severity: 'info'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const userRef = doc(db, 'users', user.uid);
      const updateData = {
        userName: formData.userName.trim(),
        userMobileNo: formData.userMobileNo ? parseInt(formData.userMobileNo) : null,
        companyName: formData.companyName.trim() || null,
        updatedAt: serverTimestamp()
      };

      await updateDoc(userRef, updateData);

      // Refresh user data in context
      if (refreshUserData) {
        await refreshUserData();
      }

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile. Please try again.',
        severity: 'info'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
      <Container maxWidth="md">

        <Paper sx={{
          p: 4,
          borderRadius: "18px",
          border: "1px solid #f0d060",
          background: "#ffffff"
        }}>

          {/* HEADER */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography sx={{
              fontSize: "1.8rem",
              fontWeight: 900,
              fontFamily: "'Unbounded', sans-serif",
              color: "#1c1400"
            }}>
              My Profile
            </Typography>

            <Typography sx={{ color: "#8a7a40" }}>
              Manage your personal information
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              {/* NAME */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  required
                  helperText="Your display name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px"
                    }
                  }}
                />
              </Grid>

              {/* MOBILE */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="userMobileNo"
                  value={formData.userMobileNo}
                  onChange={handleChange}
                  type="tel"
                  placeholder="1234567890"
                  helperText="10–12 digits (optional)"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px"
                    }
                  }}
                />
              </Grid>

              {/* COMPANY */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company / Organization"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  helperText="Optional"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px"
                    }
                  }}
                />
              </Grid>

              {/* EMAIL */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user?.userEmailID || ''}
                  disabled
                  helperText="Email cannot be changed"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      background: "#f9fafb"
                    }
                  }}
                />
              </Grid>

              {/* ROLE */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    sx={{
                      borderRadius: "10px",
                      background: "#f9fafb"
                    }}
                  >
                    <MenuItem value="visitor">Visitor</MenuItem>
                    <MenuItem value="host">Host</MenuItem>
                    <MenuItem value="appoperator">App Operator</MenuItem>
                    <MenuItem value="appadmin">App Admin</MenuItem>
                    <MenuItem value="superadmin">Super Admin</MenuItem>
                    <MenuItem value="facilityadmin">Facility Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* BUTTON */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    type="submit"
                    disabled={loading}
                    sx={{
                      minWidth: 220,
                      height: "45px",
                      borderRadius: "10px",
                      fontWeight: 700,
                      fontSize: "0.9rem",

                      background: "#1c1400",
                      color: "#fde047",

                      "&:hover": {
                        background: "#f59e0b",
                        color: "#1c1400"
                      },

                      "&:disabled": {
                        background: "#d1d5db",
                        color: "#6b7280"
                      }
                    }}
                  >
                    {loading ? "Updating..." : "Update Profile"}
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </form>

        </Paper>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default UserProfile;
